import {createSelector} from '@reduxjs/toolkit';
import {appUserSelector} from './auth.selectors';
import {currentPlanSelector, subscriptionsApiSelector} from './subscription.selectors';
import type {SubscriptionPlan} from '../../domain/types';

/**
 * The single answer to "is this user on Pro, and which plan?".
 *
 * Two stores of this fact exist and they can disagree:
 *
 *   1. Paddle, read live via `GET /rest/v1/paddle/subscriptions`. Authoritative — it is the billing
 *      system — but only available after that request lands, and only for a signed-in user.
 *   2. `subscribed` / `subscription_plan` on the users table, carried on `AppUser`. Always present
 *      the moment the profile loads, but only as fresh as whatever syncs it.
 *
 * Everything in the app reads entitlement from here so the two can never be consulted
 * independently: Paddle wins once its read succeeds, the profile columns cover the gap before that
 * and if the request fails. When the backend settles on one webhook-synced column
 * (docs/BACKEND_REQUESTS.md §8) this file is the only thing that changes.
 */

export type EntitlementSource = 'paddle' | 'profile' | 'none';

export interface Entitlement {
    isPro: boolean;
    plan: SubscriptionPlan | null;
    /** Uppercase badge text, e.g. `PRO YEARLY`. Null when the user is not on Pro. */
    planLabel: string | null;
    source: EntitlementSource;
}

const NOT_PRO: Entitlement = {isPro: false, plan: null, planLabel: null, source: 'none'};

function toLabel(plan: string | null | undefined): string {
  if (!plan) {
    return 'PRO';
  }
  return plan.replace(/_/g, ' ').toUpperCase();
}

export const entitlementSelector = createSelector(
  [currentPlanSelector, subscriptionsApiSelector, appUserSelector],
  (paddlePlan, subscriptionsApi, appUser): Entitlement => {
    // Paddle answered: trust it, including when the answer is "no active subscription".
    if (subscriptionsApi.status === 'success') {
      return paddlePlan
        ? {isPro: true, plan: paddlePlan, planLabel: toLabel(paddlePlan), source: 'paddle'}
        : NOT_PRO;
    }

    // Not asked yet, still in flight, or the request failed — fall back to the profile columns.
    if (appUser?.subscribed) {
      return {
        isPro: true,
        plan: null,
        planLabel: toLabel(appUser.subscription_plan),
        source: 'profile',
      };
    }

    return NOT_PRO;
  }
);

export const isProSelector = createSelector(
  [entitlementSelector],
  entitlement => entitlement.isPro
);

export const planLabelSelector = createSelector(
  [entitlementSelector],
  entitlement => entitlement.planLabel
);
